import { Tooltip } from '../../components';
import * as React from 'react';
import classnames from 'classnames';
import { MutationFunction } from 'react-apollo';
import { Update, UpdateVariables } from './__generated__/Update';
import { UpdatePauseStatus, UpdatePauseStatusVariables } from './__generated__/UpdatePauseStatus';
import { FufillmentCentersQuery_fulfillmentCenters as FulfillmentCenter } from './__generated__/FufillmentCentersQuery';
const IconPickup = require('./icon-pickup.png');
const IconDelivery = require('./icon-delivery.png');

const enableOrDisable = (conditional) => (conditional ? 'Disable' : 'Enable');

interface FulfillmentCenterTileProps {
  onEdit(fulfillmentCenter: FulfillmentCenter);
  onDelete(fulfillmentCenter: FulfillmentCenter);
  onClickGenerateQR(fulfillmentCenter: FulfillmentCenter);
  updatePauseStatus: MutationFunction<UpdatePauseStatus, UpdatePauseStatusVariables>;
  updateFulfillmentCenter: MutationFunction<Update, UpdateVariables>;
  fulfillmentCenter: FulfillmentCenter;
  venueId: string;
}

const PickupIndicator: React.FC<{ fulfillmentCenter: FulfillmentCenter }> = ({ fulfillmentCenter }) => {
  if (fulfillmentCenter.pickup) {
    return (
      <span className='fulfillment-centers-tile-type'>
        <img src={IconPickup} />
        <span>Pick-up</span>
      </span>
    );
  }

  return null;
};

const DeliveryIndicator: React.FC<{ fulfillmentCenter: FulfillmentCenter }> = ({ fulfillmentCenter }) => {
  if (fulfillmentCenter.delivery) {
    return (
      <span className='fulfillment-centers-tile-type'>
        <img src={IconDelivery} />
        <span>Delivery</span>
      </span>
    );
  }

  return null;
};

export class FulfillmentCenterTile extends React.Component<FulfillmentCenterTileProps, { menuOpen: boolean }> {
  constructor(props) {
    super(props);

    this.state = { menuOpen: false };
  }

  hideMenu = () => {
    this.setState({ menuOpen: false });
  };

  openMenu = () => {
    this.setState({ menuOpen: true });
  };

  editCenter = () => {
    this.props.onEdit(this.props.fulfillmentCenter);
  };

  togglePaused = () => {
    this.setState({ menuOpen: false }, () => {
      this.props.updatePauseStatus({
        variables: {
          venueId: this.props.venueId,
          fulfillmentCenterId: this.props.fulfillmentCenter.id,
          isPaused: !this.props.fulfillmentCenter.isPaused,
        },
      });
    });
  };

  togglePickup = () => {
    this.setState({ menuOpen: false }, () => {
      this.props.updateFulfillmentCenter({
        variables: {
          venueId: this.props.venueId,
          fulfillmentCenterId: this.props.fulfillmentCenter.id,
          center: {
            pickup: !this.props.fulfillmentCenter.pickup,
          },
        },
      });
    });
  };

  toggleDelivery = () => {
    this.setState({ menuOpen: false }, () => {
      this.props.updateFulfillmentCenter({
        variables: {
          venueId: this.props.venueId,
          fulfillmentCenterId: this.props.fulfillmentCenter.id,
          center: {
            delivery: !this.props.fulfillmentCenter.delivery,
          },
        },
      });
    });
  };

  toggleDisabled = () => {
    this.setState({ menuOpen: false }, () => {
      this.props.updateFulfillmentCenter({
        variables: {
          venueId: this.props.venueId,
          fulfillmentCenterId: this.props.fulfillmentCenter.id,
          center: {
            disabled: !this.props.fulfillmentCenter.disabled,
          },
        },
      });
    });
  };

  delete = () => {
    this.props.onDelete(this.props.fulfillmentCenter);
  };

  generateQrCode = () => {
    this.props.onClickGenerateQR(this.props.fulfillmentCenter);
  };

  render() {
    const { fulfillmentCenter, venueId } = this.props;

    return (
      <div
        className={classnames('fulfillment-centers-tile', {
          'fulfillment-centers-tile--disabled': fulfillmentCenter.disabled,
        })}
      >
        <div className='fulfillment-centers-tile-container'>
          <div className='d-flex justify-content-between w-100 fulfillment-centers-tile-header'>
            <p className='fulfillment-centers-tile-title'>{fulfillmentCenter.name}</p>
            <Tooltip hideMenu={this.hideMenu} openMenu={this.openMenu} open={this.state.menuOpen}>
              <Tooltip.Action onClick={this.editCenter}>Edit fulfillment center</Tooltip.Action>
              <Tooltip.Action onClick={this.togglePickup}>
                {enableOrDisable(fulfillmentCenter.pickup)} pick-up
              </Tooltip.Action>
              <Tooltip.Action onClick={this.toggleDelivery}>
                {enableOrDisable(fulfillmentCenter.delivery)} delivery
              </Tooltip.Action>
              {fulfillmentCenter.delivery && (
                <Tooltip.Action onClick={this.generateQrCode}>Generate QR Code</Tooltip.Action>
              )}
              {fulfillmentCenter.isOpen && (
                <Tooltip.Action onClick={this.togglePaused}>
                  {fulfillmentCenter.isPaused ? 'Un-pause ordering' : 'Pause ordering'}
                </Tooltip.Action>
              )}
              <Tooltip.Action onClick={this.toggleDisabled}>
                {enableOrDisable(!fulfillmentCenter.disabled)} fulfillment center
              </Tooltip.Action>
              <Tooltip.Action onClick={this.delete}>Delete fulfillment center</Tooltip.Action>
            </Tooltip>
          </div>
          {fulfillmentCenter.delivery && fulfillmentCenter.advancedDeliverySetup && (
            <a
              href={`/venue/${venueId}/fulfillment-centers/${fulfillmentCenter.id}/delivery`}
              className='fulfillment-centers-tile-manage-link'
            >
              Manage Station
            </a>
          )}
          {fulfillmentCenter.delivery && (
            <p>
              <a
                href={`/venue/${venueId}/fulfillment-centers/${fulfillmentCenter.id}/live-delivery-report`}
                className='fulfillment-centers-tile-manage-link'
              >
                Delivery Report
              </a>
            </p>
          )}
        </div>
        <div className='fulfillment-centers-tile-types'>
          <PickupIndicator fulfillmentCenter={this.props.fulfillmentCenter} />
          {this.props.fulfillmentCenter.pickup && this.props.fulfillmentCenter.delivery && (
            <span className='fulfillment-centers-tile-type-separator'>&bull;</span>
          )}
          <DeliveryIndicator fulfillmentCenter={this.props.fulfillmentCenter} />
        </div>
        {this.state.menuOpen && (
          <div className='mt-3'>
            <Tooltip.MobileMenu>
              <Tooltip.MobileAction onClick={this.editCenter}>Edit fulfillment center</Tooltip.MobileAction>
              <Tooltip.MobileAction onClick={this.togglePickup}>
                {enableOrDisable(fulfillmentCenter.pickup)} pick-up
              </Tooltip.MobileAction>
              <Tooltip.MobileAction onClick={this.toggleDelivery}>
                {enableOrDisable(fulfillmentCenter.delivery)} delivery
              </Tooltip.MobileAction>
              {fulfillmentCenter.delivery && (
                <Tooltip.MobileAction onClick={this.generateQrCode}>Generate QR Code</Tooltip.MobileAction>
              )}
              {fulfillmentCenter.isOpen && (
                <Tooltip.MobileAction onClick={this.togglePaused}>
                  {fulfillmentCenter.isPaused ? 'Un-pause ordering' : 'Pause ordering'}
                </Tooltip.MobileAction>
              )}
              <Tooltip.MobileAction onClick={this.toggleDisabled}>
                {enableOrDisable(!fulfillmentCenter.disabled)} fulfillment center
              </Tooltip.MobileAction>
              <Tooltip.MobileAction onClick={this.delete}>Delete fulfillment center</Tooltip.MobileAction>
            </Tooltip.MobileMenu>
          </div>
        )}
      </div>
    );
  }
}
